import { ReactNode } from "react";
import { motion } from "framer-motion";
import { useResponsiveMobile } from "../../../hooks/useResponsiveMobile";

const easeCubic: [number, number, number, number] = [0.22, 1, 0.36, 1];

//  이동거리만 증가
const Y_CARD = 56;
const Y_QUOTE = 56;
const Y_IMG = 72;

const Card = ({ question, answer }: { question: string | ReactNode; answer: ReactNode }) => (
  <motion.div
    className="faq-card flex flex-col gap-8 pb-10 border-b-[#DDE0E6] border-b-[1px]"
    initial={{ opacity: 0, y: Y_CARD }} //  여기만 변경
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.1, ease: easeCubic }} //  그대로
    viewport={{ once: true, amount: 0.5, margin: "0px 0px -35% 0px" }} //  그대로
  >
    <div className="flex flex-row gap-3 items-center">
      <span className="font-normal text-[28px] text-[#4D4F58] text-start self-start">Q</span>
      <span className="font-bold text-[20px] md:text-[28px] text-[#121319]">{question}</span>
    </div>
    <div className="flex flex-row gap-3">
      <span className="font-normal text-[20px] md:text-[20px] text-[#4D4F58] pl-[6px]">A</span>
      {answer}
    </div>
  </motion.div>
);

export const Section2 = () => {
  const isMobile = useResponsiveMobile(900);
  const isTablet = useResponsiveMobile(1440);
  return (
    <div className="flex w-full justify-center px-6 md:px-0 md:justify-center md:items-center md:px-[100px]">
      <div className="flex flex-col gap-20 w-full lg:max-w-[1440px]">
        <motion.div
          className="s2-quote flex flex-col gap-6 md:gap-8 self-start justify-center items-center md:items-start w-full lg:w-auto md:self-center lg:self-start"
          initial={{ opacity: 0, y: Y_QUOTE }} //  여기만 변경
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeCubic }} //  그대로
          viewport={{ once: true, amount: 0.4, margin: "0px 0px -15% 0px" }} //  그대로
        >
          <img
            width={isMobile ? 24 : 56}
            height={isMobile ? 21 : 48}
            src="/assets/images/icon/double_quotes_gray.png"
            alt=""
          />
          <div className="flex flex-col gap-6 justify-center items-center lg:items-start w-full">
            <span className="text-center font-bold text-[28px] md:text-display3_1 break-keep">
              3,000개 가맹점과 5단계 {isMobile && <br />} 영업 조직의 복잡한 정산,{" "}
              {isMobile && <br />}
              <span className="text-[#0079FF]">시스템 하나로 끝냈습니다</span>
            </span>
            <span className="font-normal text-[18px] text-[#4D4F58]">
              – 농산물 직거래 플랫폼 ‘ B마켓 ’
            </span>
          </div>
          <img
            className="self-center md:self-end"
            width={isMobile ? 24 : 56}
            height={isMobile ? 21 : 48}
            src="/assets/images/icon/double_quotes_gray2.png"
            alt=""
          />
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-[100px] md:items-start items-center">
          <motion.img
            className="s2-img block w-full md:w-auto lg:w-auto rounded-[24px] lg:max-w-[400px]"
            src={
              isMobile
                ? "/assets/images/case/eg2_mobile.png"
                : isTablet
                ? "/assets/images/case/eg2_tablet.png"
                : "/assets/images/case/eg2.png"
            }
            alt=""
            initial={{ opacity: 0, y: Y_IMG }} //  여기만 변경
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: easeCubic }} //  그대로
            viewport={{ once: true, amount: 0.35, margin: "0px 0px -10% 0px" }} //  그대로
          />

          <div className="s2-faq flex flex-col gap-10">
            <Card
              question="어떤 비즈니스를 운영하고 계신가요?"
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  산지 농가와 지역 마트, 소비자 사이의 유통 단계를 줄여주는{" "}
                  <span className="text-[#0079FF]">농산물 직거래 플랫폼</span>
                  입니다. 전국 3,000여 개의 가맹점(농가/소상공인)이 입점해 있고, 월 거래액은 약
                  200억 원 규모입니다.
                </span>
              }
            />

            <Card
              question="어떤 어려움을 겪고 계셨나요?"
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  결제사별로{" "}
                  <span className="text-[#0079FF]">정산 주기와 수수료 체계가 달라 관리팀이</span>{" "}
                  <span className="text-[#0079FF]">매일 수기 대조 업무에</span> 매달려야 했습니다.{" "}
                  엑셀로 일일이 작업하다 보니 누락이나 정산 지연이 잦았고, 이는 곧 가맹점의 신뢰
                  하락으로 이어져 고민이 컸습니다.
                </span>
              }
            />

            <Card
              question={<span>‘품’을 도입한 후 가장 체감되는 변화는 무엇인가요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  PG사와 VAN사의 모든 거래 데이터 를 한 곳에서 통합 관리할 수 있게 된 점입니다. 정산
                  오류가 제로화되면서 5명이었던 관리팀 인력을 2명으로 효율화했고,{" "}
                  <span className="text-[#0079FF]">관련 업무량은 50% 이상 감소</span>
                  했습니다.
                </span>
              }
            />

            <Card
              question={<span>영업조직 관리 측면에서는 어떤 변화가 있었나요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  저희는 총판, 지사, 대리점, 딜러 등 5단계에 달하는 복잡한 영업 대행 구조를 가지고
                  있습니다. 예전에는 단계별 수수료를 계산하는 것 자체가 일이었는데, 이제는{" "}
                  <span className="text-[#0079FF]">
                    수수료가 실시간으로 자동산정되니 일단 너무 편하더군요. 영업 대행 파트너도 직접
                    자기 실적과 정산액을 확인하니
                  </span>{" "}
                  정산 관련 불만 전화는 아예 사라지고, 문의 전화만 가끔 받으니 업무 효율이 너무
                  높아졌습니다.
                </span>
              }
            />

            <Card
              question={<span>품의 가장 큰 장점은 무엇이라고 생각하시나요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  단순한 자동화를 넘어
                  <span className="text-[#0079FF]">'조직 운영의 투명성'</span>을 만들어준다는
                  점입니다. 데이터가 투명해지니 본사와 파트너 사이의 신뢰가 단단해졌고, 이제는 운영
                  걱정 없이 가맹점 확장에만 집중하고 있습니다.
                </span>
              }
            />
          </div>
        </div>
        <motion.div
          className="s2-quote flex flex-col gap-6 md:gap-8 lg:self-start justify-center items-center md:items-start w-full md:w-auto lg:mt-[80px]"
          initial={{ opacity: 0, y: Y_QUOTE }} //  여기만 변경
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easeCubic }} //  그대로
          viewport={{ once: true, amount: 0.4, margin: "0px 0px -15% 0px" }} //  그대로
        >
          <img
            width={isMobile ? 24 : 56}
            height={isMobile ? 21 : 48}
            src="/assets/images/icon/double_quotes_gray.png"
            alt=""
          />
          <div className="flex flex-col gap-6 justify-center items-center lg:items-start w-full">
            <span className="text-center font-bold text-[28px] md:text-display3_1">
              가맹점의 자금 유동성 문제를 {isMobile && <br />}
              해결하니 <span className="text-[#0079FF]">브랜드 경쟁력</span>이 {isMobile && <br />}
              달라졌습니다
            </span>
            <span className="font-normal text-[18px] text-[#4D4F58]">
              – 치킨 프랜차이즈 ‘ D치킨 ‘
            </span>
          </div>
          <img
            className="self-center md:self-end"
            width={isMobile ? 24 : 56}
            height={isMobile ? 21 : 48}
            src="/assets/images/icon/double_quotes_gray2.png"
            alt=""
          />
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-[100px] md:items-start items-center">
          <motion.img
            className="s2-img block md:hidden w-full rounded-[24px]"
            src={
              isMobile
                ? "/assets/images/case/eg4_mobile.png"
                : isTablet
                ? "/assets/images/case/eg4_tablet.png"
                : "/assets/images/case/eg4.png"
            }
            alt=""
            initial={{ opacity: 0, y: Y_IMG }} //  여기만 변경
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: easeCubic }} //  그대로
            viewport={{ once: true, amount: 0.35, margin: "0px 0px -10% 0px" }} //  그대로
          />
          <div className="s2-faq flex flex-col gap-10">
            <Card
              question="어떤 비즈니스를 운영하고 계신가요?"
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  전국 500여 개의 매장을 운영 중인
                  <span className="text-[#0079FF]">치킨 프랜차이즈 브랜드입니다.</span> 홀 매장뿐만
                  아니라 배달 앱 등 다양한 판매 채널을 통해 월 거래액 약 150억 원이 발생하고
                  있습니다.
                </span>
              }
            />

            <Card
              question={<span>품(Poom)을 쓰기 전에는 정산 업무가 어땠나요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  판매 채널별로 거래 데이터가 파편화되어 있어 본사 차원의 일괄 관리가 매우
                  어려웠습니다.
                  <span className="text-[#0079FF]">
                    {" "}
                    정산 지급 보고서를 작성할 때마다 오류가 생겨 가맹점 입금이 늦어지는 경우가 종종
                    있었고,{" "}
                  </span>
                  현금 흐름이 중요한 점주님들의 불만이 상당했습니다.
                </span>
              }
            />

            <Card
              question={<span>‘ 품’을 도입한 후 어떤 변화가 있었나요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  <span className="text-[#0079FF]">
                    {" "}
                    별도의 대규모 시스템 구축 없이도 우리만의 특화된 정산 주기와 수수료 설정이
                    가능해진 점입니다.
                  </span>{" "}
                  모든 채널의 매출을 통합 정산 처리하니 본사 업무 효율이 극대화되었고, 가맹점별 등급
                  관리가 전산으로 명확하게 이루어지고 있습니다.
                </span>
              }
            />

            <Card
              question={
                <span>
                  시스템 도입 후 가맹점주님들과의 파트너십에서 가장 크게 달라진 점은 무엇인가요?
                </span>
              }
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  <span className="text-[#0079FF]">
                    가장 반응이 뜨거웠던 건 역시 '실시간 정산'이었어요.
                  </span>{" "}
                  본사가 매장의 돈 문제를 직접 해결해 주니까 브랜드에 대한 로열티가 눈에 띄게
                  높아졌어요. 사실 점주님들이 다른 브랜드로 옮길까 고민하다가도, 우리 정산 시스템
                  때문에라도 못 떠나겠다며 이탈 방지 효과를 톡톡히 보고 있습니다.
                </span>
              }
            />

            <Card
              question={<span>품의 가장 큰 장점은 무엇이라고 생각하시나요?</span>}
              answer={
                <span className="font-normal text-[16px] md:text-[20px] text-[#4D4F58]">
                  단순 정산 툴이 아니라 '파트너 락인(Lock-in) 솔루션' 이라는 점입니다.
                  <span className="text-[#0079FF]">
                    정산 속도와 자금 지원이 경쟁력이 되는 요즘 같은 때에, 가맹점과 상생할 수 있는
                    가장 실질적인 무기
                  </span>
                  를 가졌다고 생각합니다.
                </span>
              }
            />
          </div>
          <motion.img
            className="s2-img lg:block hidden lg:max-w-[400px] rounded-[24px]"
            src={"/assets/images/case/eg4.png"}
            alt=""
            initial={{ opacity: 0, y: Y_IMG }} //  여기만 변경
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: easeCubic }} //  그대로
            viewport={{ once: true, amount: 0.35, margin: "0px 0px -10% 0px" }} //  그대로
          />
        </div>
      </div>
    </div>
  );
};
